import { useId } from 'react';
import type { SupportAccessGrant } from '@traceio-org/platform-client';
import { NavigationGlyph } from '@traceio-org/ui';
import { formatDateTime, formatRemaining } from '../format';

/**
 * The unmistakable sign that the staff member is inside an organization's data on a support access grant:
 * what they may do, until when, and the reason every read is recorded with. It stays on every tab of the
 * organization while the grant is in force and disappears when it ends.
 */
export function SupportAccessBanner({
  grant,
  organizationName,
  now,
  onEnd,
}: {
  grant: SupportAccessGrant;
  organizationName: string;
  now: number;
  /** Ends the grant now; absent when the staff member cannot end it. */
  onEnd?: () => void;
}) {
  const headingId = useId();
  const what = grant.scope === 'write' ? 'read and change' : 'read';
  return (
    <section className="admin-access-banner" aria-labelledby={headingId} data-scope={grant.scope}>
      <span className="admin-access-banner__icon" aria-hidden="true">
        <NavigationGlyph name="Headphones" />
      </span>
      <div className="admin-access-banner__body">
        <h2 id={headingId}>Support access is on</h2>
        <p>
          You can {what} {organizationName}&apos;s data until{' '}
          <strong>
            <time dateTime={grant.expiresAt}>{formatDateTime(grant.expiresAt)}</time>
          </strong>{' '}
          ({formatRemaining(Date.parse(grant.expiresAt) - now)} left). Every read is recorded with your name
          and this reason: {grant.reason}
          {grant.ticketRef ? ` (ticket ${grant.ticketRef})` : ''}.
        </p>
      </div>
      {onEnd ? (
        <button type="button" className="admin-action" onClick={onEnd}>
          End support access now
        </button>
      ) : null}
    </section>
  );
}
